import { Drawer } from "@xola/ui-kit";
import { trimStart } from "lodash";
import React, { useEffect, useState } from "react";
import { PartnerObject, Seller, XolaPartner, XolaPartnerStatus } from "types";
import { useDebounce } from "use-debounce";
import { PartnerDistributionProfile } from "../PartnerDistributionProfile";
import { CreatePartnerConnectionModal } from "./CreatePartnerConnectionModal";
import { EditPartnerConnectionModal } from "./EditPartnerConnectionModal";
import { InfiniteList } from "@/components/Common/InfiniteList";
import { SearchInput } from "@/components/Common/SearchInput";
import { generateQueryKey } from "@/hooks/helper";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useVisibility } from "@/hooks/useVisibility";

interface AddOperatorDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

export const AddOperatorDrawer = ({ isOpen, onClose }: AddOperatorDrawerProps) => {
    return (
        <Drawer
            size="large"
            isOpen={isOpen}
            content={<DrawerContent />}
            classNames={{ dialog: "z-20", content: "mt-0", children: "!w-[540px]" }}
            onClose={onClose}
        />
    );
};

const DrawerContent = () => {
    const seller = useSeller();
    const [search, setSearch] = useState<string>();
    const [debouncedSearch] = useDebounce(search, 500);
    // TODO: use useModal hook
    const [showCreateConnectionModal, { toggle: toggleCreateConnectionModal }] = useVisibility(false);
    const [showEditConnectionModal, { toggle: toggleEditConnectionModal }] = useVisibility(false);

    const [selectedPartner, setSelectedPartner] = useState<Seller | XolaPartner>();

    const params = {
        context: "xolaPartner",
        seller: seller.id,
        ...(debouncedSearch && debouncedSearch.length > 0 && { search: debouncedSearch }),
    };

    const {
        data: sellers,
        isLoading,
        hasNextPage,
        fetchNextPage,
    } = useInfiniteCollection<Seller>("sellers", params, {
        queryKey: generateQueryKey(["partnerSellerSearch", "collection", "infinite"], params),
        keepPreviousData: false,
        shouldPopulateCache: false, // if the seller list contains current seller, it will replace the cache with this data, so adding this key
        enabled: !!(debouncedSearch && debouncedSearch.length > 2),
    });

    type SellerWithPartner = Seller & { partner?: XolaPartner; status?: XolaPartnerStatus };

    const { data: partners, isLoading: partnerIsLoading } = useAutoFetchCollection<XolaPartner>(["partners"], {
        seller: seller.id,
        expand: "purchaseRule,partnerSeller",
        object: PartnerObject.XOLA_PARTNER,
    });

    const [updatedSellers, setUpdatedSellers] = useState<SellerWithPartner[]>(sellers);

    // Update sellers with the status from matching partners
    useEffect(() => {
        if (!partnerIsLoading && partners && sellers) {
            const sellersWithStatus = sellers
                .filter((s) => s.id !== seller.id)
                .map((seller) => {
                    const matchingPartners = partners
                        .filter((partner) => partner.partnerSeller.id === seller.id)
                        .sort((a, b) => b.id.localeCompare(a.id));
                    const matchingPartner = matchingPartners.length > 0 ? matchingPartners[0] : null;
                    return matchingPartner
                        ? { ...seller, partner: matchingPartner, status: matchingPartner.status }
                        : seller;
                });
            setUpdatedSellers(sellersWithStatus);
        }
    }, [sellers, partners, partnerIsLoading, seller.id]);

    const handlePartnerClick = (seller: SellerWithPartner) => {
        if (
            seller.partner?.id &&
            [XolaPartnerStatus.ACCEPTED, XolaPartnerStatus.PENDING].includes(seller.partner?.status)
        ) {
            setSelectedPartner(seller.partner);
            toggleEditConnectionModal();
        } else {
            setSelectedPartner(seller);
            toggleCreateConnectionModal();
        }
    };

    return (
        <div className="space-y-4">
            <h1>Add Operator</h1>
            <div className="space-y-2">
                <SearchInput
                    placeholder="Search by name"
                    value={search}
                    onChange={(value) => setSearch(trimStart(value))}
                />
                {debouncedSearch && debouncedSearch?.length > 2 ? (
                    <p className="text-gray-darker">
                        <var data-var="resultsCount">{hasNextPage ? "100+" : sellers.length}</var> results for &quot;
                        {debouncedSearch}&quot;
                    </p>
                ) : null}

                <div className="h-auto">
                    <InfiniteList
                        isLoading={isLoading}
                        hasMore={hasNextPage}
                        className="divide-y divide-gray-light"
                        onLoadMore={fetchNextPage}
                    >
                        {updatedSellers?.map((seller) => (
                            <div
                                key={seller.id}
                                className="cursor-pointer py-4 px-2 hover:bg-gray-lighter"
                                onClick={() => handlePartnerClick(seller)}
                            >
                                <PartnerDistributionProfile seller={seller} status={seller.status} />
                                {/* <ChevronRightIcon size="large" className="align-middle" /> */}
                            </div>
                        ))}
                    </InfiniteList>
                </div>
            </div>
            {/* TODO: Move both create and edit into one component */}
            {selectedPartner && showCreateConnectionModal ? (
                <CreatePartnerConnectionModal
                    partnerSeller={selectedPartner as Seller}
                    isOpen={showCreateConnectionModal}
                    onClose={toggleCreateConnectionModal}
                />
            ) : null}
            {selectedPartner && showEditConnectionModal ? (
                <EditPartnerConnectionModal
                    partner={selectedPartner as XolaPartner}
                    isOpen={showEditConnectionModal}
                    onClose={toggleEditConnectionModal}
                />
            ) : null}
        </div>
    );
};
